import { API_BASE_URL, getJsonAuthHeaders } from "../../../utils/api"; // adjust to your real path to utils/api

// ─────────────────────────────────────────────────────────────────────────
// SINGLE SOURCE OF TRUTH for where teacher/campus data comes from.
//
// CAMPUSES come from your real backend (same one your Campus Management
// page already uses: GET/POST/PUT/DELETE /api/campuses with auth headers).
//
// TEACHERS still live in localStorage for now. When you eventually build a
// teachers backend, only the TEACHERS section below needs to change — the
// campus logic is already wired to your real API.
// ─────────────────────────────────────────────────────────────────────────

const TEACHERS_KEY = "als_teachers";

// Old fake campus data used to live under this key. We proactively clear it
// once so it can never again shadow the real backend campuses or confuse a
// future debugging session.
const LEGACY_CAMPUSES_KEY = "als_campuses";
try {
  localStorage.removeItem(LEGACY_CAMPUSES_KEY);
} catch {
  /* ignore — non-browser environment or storage disabled */
}

// Fired on every write so any mounted component can react immediately
// (e.g. Teacher Reports refreshing right after Teachers page saves someone).
export const ALS_DATA_CHANGED_EVENT = "als-data-changed";

function broadcastChange(key) {
  window.dispatchEvent(
    new CustomEvent(ALS_DATA_CHANGED_EVENT, { detail: { key } }),
  );
}

function readLocal(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch (err) {
    console.error(`Failed to parse localStorage key "${key}"`, err);
    return fallback;
  }
}

function writeLocal(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    broadcastChange(key);
  } catch (err) {
    console.error(`Failed to write localStorage key "${key}"`, err);
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────────────
// CAMPUSES — real backend, same pattern as your Campus Management page
// ─────────────────────────────────────────────────────────────────────────

export async function fetchCampuses() {
  const res = await fetch(`${API_BASE_URL}/api/campuses`, {
    headers: getJsonAuthHeaders(),
  });

  if (res.status === 401) {
    // Let the caller decide what to do (e.g. log the user out). We throw a
    // typed error so pages can special-case it if they want to.
    const err = new Error("Unauthorized");
    err.status = 401;
    throw err;
  }
  if (!res.ok) {
    throw new Error(`Failed to fetch campuses (${res.status})`);
  }

  // Expected shape: [{ id, name, location, principal, ... }, ...]
  return res.json();
}

// ─────────────────────────────────────────────────────────────────────────
// TEACHERS — real backend-backed CRUD
// ─────────────────────────────────────────────────────────────────────────

export async function fetchTeachers() {
  const res = await fetch(`${API_BASE_URL}/api/teachers`, {
    headers: getJsonAuthHeaders(),
  });

  if (res.status === 401) {
    const err = new Error("Unauthorized");
    err.status = 401;
    throw err;
  }

  if (!res.ok) {
    let detail = "Failed to fetch teachers";
    try {
      const data = await res.json();
      detail = data.detail || detail;
    } catch {
      // ignore JSON parse errors, fall back to the default message
    }
    throw new Error(detail);
  }

  return res.json();
}

export async function createTeacher(payload) {
  const res = await fetch(`${API_BASE_URL}/api/teachers`, {
    method: "POST",
    headers: getJsonAuthHeaders(),
    body: JSON.stringify(payload),
  });

  if (res.status === 401) {
    const err = new Error("Unauthorized");
    err.status = 401;
    throw err;
  }

  if (!res.ok) {
    let detail = "Failed to create teacher";
    try {
      const data = await res.json();
      detail = data.detail || detail;
    } catch {
      // ignore JSON parse errors, fall back to the default message
    }
    throw new Error(detail);
  }

  return res.json();
}

export async function updateTeacher(teacherId, payload) {
  const res = await fetch(
    `${API_BASE_URL}/api/teachers/${encodeURIComponent(teacherId)}`,
    {
      method: "PUT",
      headers: getJsonAuthHeaders(),
      body: JSON.stringify(payload),
    },
  );

  if (res.status === 401) {
    const err = new Error("Unauthorized");
    err.status = 401;
    throw err;
  }

  if (!res.ok) {
    let detail = "Failed to update teacher";
    try {
      const data = await res.json();
      detail = data.detail || detail;
    } catch {
      // ignore JSON parse errors, fall back to the default message
    }
    throw new Error(detail);
  }

  return res.json();
}

export async function deleteTeacher(teacherId) {
  const res = await fetch(
    `${API_BASE_URL}/api/teachers/${encodeURIComponent(teacherId)}`,
    {
      method: "DELETE",
      headers: getJsonAuthHeaders(),
    },
  );

  if (res.status === 401) {
    const err = new Error("Unauthorized");
    err.status = 401;
    throw err;
  }

  if (!res.ok) {
    let detail = "Failed to delete teacher";
    try {
      const data = await res.json();
      detail = data.detail || detail;
    } catch {
      // ignore JSON parse errors, fall back to the default message
    }
    throw new Error(detail);
  }
}

export async function findTeacherByCnic(cnic) {
  const teachers = await fetchTeachers();
  return teachers.find(
    (t) => t.cnic && cnic && String(t.cnic).trim() === String(cnic).trim(),
  );
}

/** All teacher ↔ course assignments (optional filters via query string). */
export async function fetchTeacherCourseAssignments(params = {}) {
  const query = new URLSearchParams();
  if (params.teacher_id) query.set("teacher_id", String(params.teacher_id));
  if (params.campus_id) query.set("campus_id", String(params.campus_id));
  if (params.course_id) query.set("course_id", String(params.course_id));

  const qs = query.toString();
  const res = await fetch(
    `${API_BASE_URL}/api/teacher-courses${qs ? `?${qs}` : ""}`,
    { headers: getJsonAuthHeaders() },
  );

  if (res.status === 401) {
    const err = new Error("Unauthorized");
    err.status = 401;
    throw err;
  }
  if (!res.ok) {
    let detail = "Failed to fetch teacher course assignments";
    try {
      const data = await res.json();
      detail = data.detail || detail;
    } catch {
      // ignore
    }
    throw new Error(detail);
  }

  return res.json();
}

/** Attach assigned_courses[] onto each teacher from /api/teacher-courses. */
export function mergeTeacherCourseAssignments(teachers = [], assignments = []) {
  const byTeacherId = new Map();
  (assignments || []).forEach((a) => {
    const key = String(a.teacher_id);
    if (!byTeacherId.has(key)) byTeacherId.set(key, []);
    byTeacherId.get(key).push({
      course_id: a.course_id,
      course_name: a.course_name || null,
      class_id: a.class_id || null,
      class_name: a.class_name || null,
      campus_id: a.campus_id || null,
    });
  });

  return (teachers || []).map((t) => {
    const fromApi = Array.isArray(t.assigned_courses) ? t.assigned_courses : [];
    const fromAssignments = byTeacherId.get(String(t.id)) || [];
    const assigned_courses = fromApi.length ? fromApi : fromAssignments;
    return { ...t, assigned_courses };
  });
}
